/**
 * Theme preference: which color theme the console renders.
 *
 * The preference is a per-browser convenience (not account data), so it lives
 * in localStorage. `public/theme-init.js` applies the same rule before first
 * paint; keep THEME_STORAGE_KEY and the resolution rule in sync with it
 * (enforced by themePreference.test.ts).
 */

export type ThemePreference = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'so-theme-preference';
export const THEME_ATTRIBUTE = 'data-theme';
export const THEME_PREFERENCE_ATTRIBUTE = 'data-theme-preference';
export const DEFAULT_THEME_PREFERENCE: ThemePreference = 'light';
/**
 * Dark and "Match system" are offered: the public pages carry no raw palette
 * classes (enforced by src/__tests__/publicRoutePalette.test.ts), so every
 * public page follows the theme tokens (console UX overhaul, decision 4 and
 * Task 11). Light stays the default; people opt in from the account menu.
 * When false, every preference resolves to light and the picker is hidden.
 * `public/theme-init.js` mirrors this.
 */
export const DARK_THEME_AVAILABLE = true;
const DARK_QUERY = '(prefers-color-scheme: dark)';

export const THEME_PREFERENCES: readonly ThemePreference[] = ['system', 'light', 'dark'];

const isThemePreference = (value: unknown): value is ThemePreference =>
  value === 'system' || value === 'light' || value === 'dark';

export function readThemePreference(darkAvailable = DARK_THEME_AVAILABLE): ThemePreference {
  if (!darkAvailable) return DEFAULT_THEME_PREFERENCE;
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isThemePreference(stored) ? stored : DEFAULT_THEME_PREFERENCE;
  } catch {
    return DEFAULT_THEME_PREFERENCE;
  }
}

const systemPrefersDark = (): boolean =>
  typeof window.matchMedia === 'function' && window.matchMedia(DARK_QUERY).matches;

export function resolveTheme(
  preference: ThemePreference,
  darkAvailable = DARK_THEME_AVAILABLE,
): ResolvedTheme {
  if (!darkAvailable) return 'light';
  if (preference === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return preference;
}

export function applyThemePreference(preference: ThemePreference): ResolvedTheme {
  const resolved = resolveTheme(preference);
  const root = document.documentElement;
  root.setAttribute(THEME_ATTRIBUTE, resolved);
  root.setAttribute(THEME_PREFERENCE_ATTRIBUTE, preference);
  return resolved;
}

type Listener = () => void;
const listeners = new Set<Listener>();
let currentPreference: ThemePreference | null = null;

const notify = () => listeners.forEach((listener) => listener());

export function getThemePreference(): ThemePreference {
  if (currentPreference === null) currentPreference = readThemePreference();
  return currentPreference;
}

export function setThemePreference(preference: ThemePreference): void {
  currentPreference = preference;
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // Storage can be unavailable (private mode); the choice still applies now.
  }
  applyThemePreference(preference);
  notify();
}

export function subscribeThemePreference(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Re-resolve "system" when the OS theme changes. Returns an unsubscribe. */
export function watchSystemTheme(): () => void {
  if (typeof window.matchMedia !== 'function') return () => undefined;
  const query = window.matchMedia(DARK_QUERY);
  const onChange = () => {
    if (getThemePreference() === 'system') {
      applyThemePreference('system');
      notify();
    }
  };
  query.addEventListener('change', onChange);
  return () => query.removeEventListener('change', onChange);
}
